import { DataGrid } from '@mui/x-data-grid'
import { Link, useNavigate } from 'react-router-dom'
import { useSearchParams } from 'react-router-dom'
import FormHelperText from '@mui/material/FormHelperText'

import { Box, IconButton, Chip, Avatar } from '@mui/material'
import Button from '@mui/material/Button'
import * as React from 'react'
import { useQuery } from '@apollo/client'
import { getAllUsers, getAllvideos, getAllSets } from '../../graphql/queries'
import { useState, useEffect } from 'react'
import ProgressBar from '../circularProgress/circularProgress'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import ImageIcon from '@mui/icons-material/Image'

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'starting':
      return 'info'
    case 'processing':
      return 'warning'
    case 'succeeded':
      return 'success'
    case 'canceled':
      return 'default'
    case 'failed':
      return 'error'
    default:
      return 'default'
  }
}

const getStatusLabel = (status) => {
  if (!status) return 'Unknown'
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

const getTypeLabel = (type) => {
  if (!type) return 'Unknown'
  // Map the type to display format
  const typeMap = {
    'PACK': 'Pack',
    'IMAGE': 'img2img',
    'VIDEO': 'text2Video',
    'IMG2IMG': 'img2img'
  }
  return typeMap[type] || type
}

const columns = [
  {
    field: 'thumbnail',
    headerName: 'Thumbnail',
    flex: 0.5,
    minWidth: 80,
    filterable: false,
    sortable: false,
    renderCell: params => {
      const { images, videos, type } = params.row
      let thumbnailUrl = null
      
      // Get thumbnail based on type
      if (type === 'VIDEO' && videos && videos.length > 0) {
        thumbnailUrl = videos[0].thumbnailUrl
      } else if (images && images.length > 0) {
        thumbnailUrl = images[0].url
      }

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
          {thumbnailUrl ? (
            <Avatar 
              src={thumbnailUrl} 
              variant="rounded"
              sx={{ width: 45, height: 45 }}
            >
              <ImageIcon />
            </Avatar>
          ) : (
            <Box sx={{ color: '#999' }}>
              <ImageIcon fontSize="small" />
            </Box>
          )}
        </Box>
      )
    },
  },
  {
    field: 'name',
    headerName: 'Prompt',
    flex: 2,
    minWidth: 200,
    sortable: false,
    renderCell: params => (
      <Box sx={{ 
        overflow: 'hidden', 
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}>
        {params.value || '-'}
      </Box>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    minWidth: 120,
    sortable: false,
    renderCell: params => (
      <Chip
        label={getStatusLabel(params.value)}
        color={getStatusColor(params.value)}
        size="small"
        variant="filled"
        sx={{
          fontWeight: 'bold',
          minWidth: '80px',
          '& .MuiChip-label': {
            px: 1.5
          }
        }}
      />
    ),
  },
  {
    field: 'model',
    headerName: 'Model',
    flex: 1.5,
    minWidth: 150,
    sortable: false,
    renderCell: params => (
      <Box sx={{ 
        overflow: 'hidden', 
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}>
        {params.value?.version ? params.value.version.substring(0, 20) + '...' : '-'}
      </Box>
    ),
  },
  {
    field: 'type',
    headerName: 'Type',
    flex: 1,
    minWidth: 120,
    sortable: false,
    renderCell: params => (
      <Chip
        label={getTypeLabel(params.value)}
        size="small"
        variant="outlined"
        color="primary"
        sx={{
          fontWeight: 'bold',
        }}
      />
    ),
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    sortable: false,
    flex: 1.5,
    minWidth: 170,
    valueGetter: params => {
      const date = new Date(params.row.createdAt)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
  },
]

export default function SetTable() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [sortModel, setSortModel] = useState([{ field: 'createdAt', sort: 'desc' }])
  const initialStatus = searchParams.get('status') || ''
  const [selectedStatus, setSelectedStatus] = useState(initialStatus)
  const initialType = searchParams.get('type') || ''
  const [selectedType, setSelectedType] = useState(initialType)
  const initialPage = parseInt(searchParams.get('page')) || 0
  const initialPageSize = parseInt(searchParams.get('pageSize')) || 50
  const [page, setPage] = useState(initialPage)
  const [pageSize, setPageSize] = useState(initialPageSize)

  useEffect(() => {
    const params = {}
    if (page !== 0) params.page = page.toString()
    if (pageSize !== 50) params.pageSize = pageSize.toString()
    if (selectedStatus) params.status = selectedStatus
    if (selectedType) params.type = selectedType
    setSearchParams(params)
  }, [page, pageSize, selectedStatus, selectedType])

  const { loading, error, data } = useQuery(getAllSets, {
    variables: {
      page: page + 1,
      pageSize,
      status: selectedStatus ? selectedStatus : null,
      type: selectedType ? selectedType : null,
      sortField: sortModel[0]?.field || null,
      sortDirection: sortModel[0]?.sort || null,
    },
    fetchPolicy: 'cache-and-network',
  })

  if (loading) {
    return <ProgressBar />
  }

  const handleRowClick = params => {
    if (params.id) {
      navigate(`/sets/${params.id}`)
    }
  }

  const rowsData = data?.getAllSetsAdmin?.sets ?? []
  const totalCount = data?.getAllSetsAdmin?.totalCount ?? 0

  const processedRows = rowsData.map((row, index) => ({
    ...row,
    tableIndex: page * pageSize + index + 1,
  }))

  const handlePaginationModelChange = newModel => {
    setPage(newModel.page)
    setPageSize(newModel.pageSize)
  }

  if (error) {
    return <p>Error: {error.message}</p>
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column-reverse' }}>
          <Select
            variant="standard"
            name="status"
            value={selectedStatus}
            onChange={e => setSelectedStatus(e.target.value)}
            sx={{ width: '250px' }}
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="starting">Starting</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="succeeded">Succeeded</MenuItem>
            <MenuItem value="canceled">Canceled</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
          <FormHelperText>Filter status</FormHelperText>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column-reverse' }}>
          <Select
            variant="standard"
            name="type"
            value={selectedType}
            onChange={e => setSelectedType(e.target.value)}
            sx={{ width: '250px' }}
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="IMAGE">Image</MenuItem>
            <MenuItem value="VIDEO">Video</MenuItem>
            <MenuItem value="PACK">Pack</MenuItem>
          </Select>
          <FormHelperText>Filter type</FormHelperText>
        </Box>
      </Box>
      <DataGrid
        disableColumnMenu
        disableColumnSorting
        pagination
        paginationMode="server"
        rowCount={totalCount}
        page={page}
        onPageChange={newPage => {
          setPage(newPage)
          setPage(newPage)
        }}
        pageSize={pageSize}
        onPageSizeChange={newSize => {
          setPageSize(newSize)
          setPage(0)
        }}
        rowsPerPageOptions={[25, 50, 100]}
        rows={processedRows}
        columns={columns}
        loading={loading}
        onRowClick={handleRowClick}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[25, 50, 100]}
        getRowHeight={() => 65}
        sx={{
          height: 680,
          '& .MuiDataGrid-virtualScroller': {
            msOverflowY: 'auto',
          },
          fontSize: '13px',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
          '.MuiFormControl-root': {
            margin: '0px 0px 10px 0px',
            width: '100px',
          },
          '.MuiOutlinedInput-root': {
            width: '150px',
          },
          '.MuiToolbar-regular': {
            padding: 0,
          },
        }}
      />
    </Box>
  )
}
